import React, { useEffect, useState, useRef } from "react"
import Api from "../../../../_helpers/Api"
import Skeleton from "react-loading-skeleton"
import { trans } from "../../../../_providers/Translation"
import "../../../../assets/css/partials/status-diagram.css"
import "../../../../assets/css/partials/StoragePartsStats.css"
import { PieChart } from "@mui/x-charts"
import { Button } from "@mui/material"
import ContractorsList from "./ContractorsList"
import CompactFilterLayout from "@/components/partials/CompactFilterLayout"
import DateRange from "@/components/partials/DateRange"
import filterImage from "@/assets/img/icons/filter.svg"
import AddOrEdit from "../../contractors/list/AddOrEdit"
import { useCalculateColor } from "../../../../_helpers/useCalculateColor"

const INITIAL_FILTERS = {
  date_from: null,
  date_to: null,
}

export default function ContractorsChart({
  endpoint = "dashboard/storage-parts/stats",
  size = 220,
}) {
  const [state, setState] = useState({ loading: true, data: null })
  const [filters, setFilters] = useState(INITIAL_FILTERS)
  const [refreshKey, setRefreshKey] = useState(0)

  const compactFilterLayoutRef = useRef(null)
  const fetchDebounceRef = useRef(null)
  const addOrEditRef = useRef(null)

  const { getColorVariation, gradientId, gradientUrl } = useCalculateColor("contractors-gradient")

  useEffect(() => {
    clearTimeout(fetchDebounceRef.current)
    fetchDebounceRef.current = setTimeout(() => {
      setState((prev) => ({ ...prev, loading: true }))

      let mounted = true

      Api.get(
        `dashboard/contractors?date_from=${filters.date_from || ""}&date_to=${filters.date_to || ""}`,
      )
        .then((res) => {
          if (!mounted) return
          const payload = res?.data ?? res
          setState({ loading: false, data: payload })
        })
        .catch((error) => {
          console.error("ContractorsChart API Error:", error)
          if (mounted) setState({ loading: false, data: null })
        })

      return () => {
        mounted = false
      }
    }, 300)
    return () => clearTimeout(fetchDebounceRef.current)
  }, [filters, refreshKey])

  function handleFilterChange(name, value) {
    setFilters((prev) => ({ ...prev, [name]: value }))
  }

  function getAppliedFiltersCount() {
    return filters.date_from || filters.date_to ? 1 : 0
  }

  const handleCreate = () => {
    setRefreshKey(prev => prev + 1)
  }

  const handleUpdate = () => {
    setRefreshKey(prev => prev + 1)
  }

  const apiData = state.data || {}
  const statsData = Array.isArray(apiData.types_stats?.types)
    ? apiData.types_stats.types
    : []
  const recentParts = Array.isArray(apiData.overview?.contractors)
    ? apiData.overview.contractors
    : []

  // For each slice: color1 is the base color (used for the legend),
  // color2 is the base color lightened a bit — both feed the gradient.
  function getSliceColors(color) {
    const color1 = color
    const color2 = color ? getColorVariation(3, color) : color
    return { color1, color2 }
  }

  // Restore the base color on the tooltip swatch (a gradient url() can't be
  // used as a background-color, so the default marker would render empty).
  function PieTooltipContent({ itemData, series }) {
    const point = series.data[itemData.dataIndex]
    const baseColor = statsData[itemData.dataIndex]?.color
    return (
      <div className="status-pie-tooltip" style={{
        backgroundColor: "#1E1B39",
        borderRadius: "10px"
      }}>
        <span className="marker" style={{ backgroundColor: baseColor }} />
        <span className="label">{point?.label}</span>
        <span className="value" style={{ color: "#fff" }}>{point?.value}</span>
      </div>
    )
  }

  return (
    <>
    <AddOrEdit
      ref={addOrEditRef}
      handleCreate={handleCreate}
      handleUpdate={handleUpdate}
    />
    <div className="storage-parts-stats-chart">
      <div className="head">
        <h3>{trans("nav.contractors")}</h3>
        <div className="right">
          <button
            onClick={() => addOrEditRef.current.add()}
            className="button button-add"
            style={{ marginRight: "0" }}
          >
            {trans("buttons.new")}
          </button>
          <Button
            onClick={(e) => {
              compactFilterLayoutRef.current.open(e)
            }}
          >
            <img src={filterImage} alt="Filter" />
            {getAppliedFiltersCount() ? (
              <i>{getAppliedFiltersCount()}</i>
            ) : (
              <></>
            )}
          </Button>
          <CompactFilterLayout
            ref={compactFilterLayoutRef}
            appliedFiltersCount={getAppliedFiltersCount()}
            onClearClick={() => setFilters(INITIAL_FILTERS)}
          >
            <div className="row">
              <div className="col">
                <DateRange
                  startDate={filters.date_from || ""}
                  endDate={filters.date_to || ""}
                  onStartDateChange={(date) =>
                    handleFilterChange("date_from", date)
                  }
                  onEndDateChange={(date) =>
                    handleFilterChange("date_to", date)
                  }
                />
              </div>
            </div>
          </CompactFilterLayout>
        </div>
      </div>

      <hr />

      <div className="body">
        <div className="left">
          <h5>{trans("random.recentlyAdded")}</h5>
          <ContractorsList recentParts={recentParts} loading={state.loading} />
        </div>
        <div className="right">
          {state.loading ? (
            <Skeleton height={150} count={2} />
          ) : statsData.length === 0 ? (
            <div
              className="alert warning"
              style={{
                width: "100%",
                marginBottom: "auto",
              }}
            >
              {trans("messages.noDataFound")}
            </div>
          ) : (
            <div className="bot">
              <div className="bot-top">
                <h5>{trans("random.companiesByType")}</h5>
              </div>
              <div className="bot-chart">
                <PieChart
                  series={[
                    {
                      data: statsData.map((stat, index) => {
                        return {
                          id: stat.type_id,
                          value: stat.contractors_count || 0,
                          label: stat.type_name,
                          color: stat.color
                            ? gradientUrl(index)
                            : stat.color,
                        }
                      }),
                    },
                  ]}
                  sx={{
                    "& .MuiPieArc-root": {
                      stroke: "none",
                      strokeWidth: 0,
                    },
                  }}
                  width={size}
                  height={size}
                  margin={{ right: 0 }}
                  slotProps={{
                    legend: { hidden: true },
                  }}
                  slots={{
                    itemContent: PieTooltipContent,
                  }}
                >
                  <defs>
                    {statsData.map((stat, index) => {
                      const { color1, color2 } = getSliceColors(stat.color)
                      
                      return (
                        <linearGradient
                          key={index}
                          id={gradientId(index)}
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop offset="0%" stopColor={color1} />
                          <stop offset="100%" stopColor={color2} />
                        </linearGradient>
                      )
                    })}
                  </defs>
                </PieChart>
                <table>
                  <tbody>
                    {statsData.map((stat, idx) => {
                      return (
                        <tr key={stat.name || idx}>
                          <td>
                            <span
                              style={{ backgroundColor: stat.color }}
                            ></span>
                          </td>
                          <td>
                            <b>{stat.type_name}</b>
                          </td>
                          <td>{stat.contractors_count}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  )
}
